import type React from "react";
import { motion } from "framer-motion";
import { ChainGrid } from "./ChainCard";
import TelegramIcon from "assets/svgComponents/PresaleBlock/TelegramIcon";
import XIcon from "assets/svgComponents/PresaleBlock/XIcon";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { QuestionButton } from "ui-kit/QuestionButton";
import { Button } from "ui-kit/Button";
import { appStore } from "store";
import AGNTHubIcon from "../../../../../assets/svgComponents/PresaleBlock/AGNTHubIcon.svg";
import { PresaleTop } from "./PresalaCardTopPart";
import { useTranslation } from "react-i18next";
import { AutoTextSize } from "auto-text-size";
import { useWindowSize } from "usehooks-ts";
import clsx from "clsx";
import {
  FontSizeType,
  getAdaptiveFontSize,
  getResponsiveFontSize,
} from "utils/getResposiveTextSizes";
import {
  AnimatedGradientBorder,
  GlowCard,
} from "screens/Landing/components/shared";
import { Link } from "react-router-dom";
import SubscribersIcon from "assets/svgComponents/PresaleBlock/SubscribersIcon";
import ArrowAhead from "assets/svgComponents/PresaleBlock/ArrowAhead";
import { shadowAndBorderClass } from "screens/Dashboard/utils/classes";
import DiscordIcon from "assets/svgComponents/PresaleBlock/DiscordIcon";
import { Wallet } from "lucide-react";

const socialIcons = [
  {
    id: 1,
    linkUrl: "https://t.me/agnt_ann",
    subscribers: "167,267",
    icon: TelegramIcon,
    name: "Telegram",
  },
  {
    id: 2,
    linkUrl: "https://x.com/agnt_hub",
    subscribers: "105,500",
    icon: XIcon,
    name: "X (Twitter)",
  },
  {
    id: 3,
    linkUrl: "https://discord.gg/agnthub",
    subscribers: "98,909",
    icon: DiscordIcon,
    name: "Discord",
  },
];

interface PresaleCardProps {
  targetDate?: string;
  isDashboard?: boolean;
}

export const PresaleCard: React.FC<PresaleCardProps> = ({
  targetDate = "2024-12-31T23:59:59",
  isDashboard = false,
}) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isMobile = width < 1024;
  const { currentRound, isLoading } = appStore();
  const { openConnectModal } = useConnectModal();

  const cardAnimation = {
    initial: { opacity: 0, y: "20px", scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: 0.8, ease: "easeOut" },
  };

  const customShadow = "shadow-[0px_6px_7.5px_0px_rgba(0,0,0,0.06)]";

  return (
    <motion.div
      {...cardAnimation}
      className={clsx(
        "flex relative z-20 flex-col gap-[16px] max-lg:gap-[16px] w-[445px] max-lg:w-full lg:max-w-[445px]",
        isDashboard && "max-lg:mb-[64px]",
      )}
    >
      <AnimatedGradientBorder className="rounded-[28px] max-lg:rounded-[28px]">
        <GlowCard className="flex flex-col gap-[15px] max-lg:gap-[15px] rounded-[28px] max-lg:rounded-[28px] bg-[#111111] w-[445px] max-lg:w-full p-[12px] max-lg:p-[12px]">
          <PresaleTop
            iResponsible={false}
            targetDate={targetDate}
            currentRound={currentRound}
            hideUsdRaised={false}
          />
          {isDashboard ? (
            <div className="relative">
              <Button
                onClick={() => {
                  if (openConnectModal) {
                    openConnectModal();
                  }
                }}
                className="w-full inline-flex items-center justify-between gap-[12px] pl-[17px] pr-[12px] max-lg:pr-[12px] max-lg:!py-[12px] max-lg:pl-[12px] !py-[14px] h-auto !rounded-full
                        border-none shadow-[0px_6px_7.5px_0px_#0000000f]
                        bg-white hover:opacity-90 transition-opacity"
              >
                <span className="font-luxora-medium font-normal text-black text-[16px] max-lg:text-[16px] tracking-[0] leading-[110%] whitespace-nowrap">
                  {t("header.connectWalletforAllocation")}
                </span>
                <Wallet className="w-[18px] h-[18px] max-lg:w-[18px] max-lg:h-[18px] text-black shrink-0" />
              </Button>
            </div>
          ) : (
            <div className="relative">
              <Link to={"/dashboard"}>
                <Button
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                  className="w-full inline-flex items-center justify-between gap-[12px] pl-[17px] pr-[8px] max-lg:pr-[8px] max-lg:!py-[8px] max-lg:pl-[12px] !py-[8px] h-auto !rounded-[16px] max-lg:!rounded-[12px]
                        border-none shadow-[0px_6px_7.5px_0px_#0000000f]
                        bg-[linear-gradient(90deg,rgba(255,166,134,1)_0%,rgba(255,173,174,1)_40%,rgba(207,178,255,1)_100%),linear-gradient(0deg,rgba(233,235,243,1)_0%,rgba(233,235,243,1)_100%)]
                        before:content-[''] before:absolute before:inset-0 before:p-px before:rounded-[16px] max-lg:before:rounded-[9px] before:[background:linear-gradient(107deg,rgba(255,255,255,1)_0%,rgba(255,255,255,0)_100%)]
                        before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] before:[-webkit-mask-composite:xor]
                        before:[mask-composite:exclude] before:z-[1] before:pointer-events-none hover:opacity-90 transition-opacity"
                >
                  <span className="font-luxora-medium  font-normal text-white text-[16px] max-lg:text-[16px] tracking-[0] leading-[110%] whitespace-nowrap">
                    {t("presaleCard.getAttsToken")}
                  </span>
                  <div className="flex bg-white rounded-[8px] max-lg:rounded-[8px] items-center justify-center w-[32px] h-[32px] max-lg:w-[31px] max-lg:h-[31px]">
                    <img src={AGNTHubIcon} alt="" />
                  </div>
                </Button>
              </Link>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: "20px" }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
          >
            <div className="flex items-center justify-center mb-[16px] max-lg:mb-[16px]">
              <div className="flex-1 h-px bg-white/10"></div>
              <span className="text-white/40 text-[14px] font-luxora-regular max-lg:text-[14px] p-[0_16px] max-lg:p-[0_16px]">
                {t("presaleCard.paymentMethods")}
              </span>
              <div className="flex-1 h-px bg-white/10"></div>
            </div>

            <ChainGrid />
          </motion.div>
          <div className="flex flex-row gap-[8px] max-lg:gap-[8px] w-full">
            <QuestionButton
              label={t("presaleCard.howToBuy")}
              link="https://t.me/agnthub_support_bot "
              isQuestion={true}
            />
            {!isDashboard && (
              <QuestionButton
                label={t("presaleCard.inviteFriends")}
                link="/referrals"
              />
            )}
          </div>
        </GlowCard>
      </AnimatedGradientBorder>

      <div className="relative translate-y-[-10px] max-lg:translate-y-[-10px] animate-fade-in opacity-0 [--animation-delay:200ms]">
        <AnimatedGradientBorder className="rounded-[16px] max-lg:rounded-[24px] w-full">
          <GlowCard className="flex flex-col items-start gap-[15px] max-lg:gap-[15px] p-[12px] max-lg:p-[12px] w-full bg-[#111111] rounded-[16px] max-lg:rounded-[24px]">
            <div className="p-0 w-full">
              <div
                className={`relative flex  pl-[16px] h-[47px] justify-start items-center max-lg:h-[47px]  gap-[3px] max-lg:gap-[3px] p-[3px] w-full rounded-[16px] max-lg:rounded-[12px] before:!rounded-[16px] max-lg:before:!rounded-[12px] border-[none] ${customShadow}
              bg-[linear-gradient(90deg,rgba(207,178,255,0.16)_0%,rgba(255,173,174,0.16)_60%,rgba(255,166,134,0.16)_100%),linear-gradient(0deg,rgba(26,26,26,1)_0%,rgba(26,26,26,1)_100%)]
              ${shadowAndBorderClass} `}
              >
                <AutoTextSize
                  minFontSizePx={getAdaptiveFontSize(
                    isMobile ? 5.33 : 1.39,
                    t("presaleCard.ourSocialMedia").length,
                    width,
                    FontSizeType.SHORT,
                  )}
                  maxFontSizePx={getResponsiveFontSize(
                    isMobile ? 5.33 : 1.39,
                    width,
                  )}
                  as={"p"}
                  className="w-full my-auto flex items-center max-lg:break-words lg:!whitespace-nowrap"
                >
                  <div className="text-[20px] font-luxora-medium">
                    {" "}
                    {t("presaleCard.ourSocialMedia")}
                  </div>
                </AutoTextSize>
              </div>

              <div className="flex-col mt-[16px] max-lg:mt-[16px] space-y-[4px] max-lg:space-y-1">
                {socialIcons.map((item) => {
                  const Icon = item.icon;

                  return (
                    <a
                      key={item.id}
                      href={item.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${shadowAndBorderClass} before:!rounded-[16px] max-lg:before:!rounded-[16px] rounded-[16px] max-lg:rounded-[16px] px-[8px] max-lg:px-[8px] min-h-[40px] max-lg:min-h-[40px]
                    h-[40px] max-lg:h-[40px] w-full flex items-center bg-[#252525] justify-between`}
                    >
                      <div className="flex item-center gap-[12px] max-lg:gap-[12px]">
                        <Icon className="max-lg:w-[20px] max-lg:h-[20px] my-auto text-white" />

                        <div className="text-white/40 leading-[110%] flex items-center text-[16px] max-lg:text-[16px]">
                          {item.name}
                        </div>
                      </div>

                      <div className="flex items-center justify-between max-lg:w-[114px] w-[122px]">
                        <div className="flex items-center">
                          {" "}
                          <SubscribersIcon />
                          <div className="ml-[4px] max-lg:ml-[4px] mr-[8px] max-lg:mr-[8px] text-white leading-[110%] text-[16px] max-lg:text-[16px]">
                            {item.subscribers}
                          </div>
                        </div>
                        <div
                          className={clsx(
                            "w-[24px] max-lg:w-[24px] h-[24px] max-lg:h-[24px] flex items-center justify-center rounded-full before:!rounded-full bg-[#333333]",
                            shadowAndBorderClass,
                          )}
                        >
                          <ArrowAhead className="text-white" />
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          </GlowCard>
        </AnimatedGradientBorder>
      </div>
    </motion.div>
  );
};
